import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import Swal from 'sweetalert2';

export default function RadiologyIndex() {
    const { t } = useContext(TranslatorContext);

    const [parentRadiologies, setParentRadiologies] = useState([]);
    const [childRadiologies, setChildRadiologies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedParent, setSelectedParent] = useState(null);
    const [currentId, setCurrentId] = useState(null);
    const [currentType, setCurrentType] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Store Modal States (for adding child radiology)
    const [storeModal, setStoreModal] = useState(false);
    const [storeForm, setStoreForm] = useState({ name: '', price: '', cost_price: '' });
    const [storeErrors, setStoreErrors] = useState({});
    const [storeLoading, setStoreLoading] = useState(false);

    // Update Child Modal States
    const [updateModal, setUpdateModal] = useState(false);
    const [updateForm, setUpdateForm] = useState({ id: null, name: '', price: '', cost_price: '' });
    const [updateErrors, setUpdateErrors] = useState({});
    const [updateLoading, setUpdateLoading] = useState(false);

    // Update Parent Modal States (for editing parent category)
    const [updateParentModal, setUpdateParentModal] = useState(false);
    const [updateParentForm, setUpdateParentForm] = useState({ id: null, name: '' });
    const [updateParentErrors, setUpdateParentErrors] = useState({});
    const [updateParentLoading, setUpdateParentLoading] = useState(false);

    // Add Parent Modal States
    const [addParentModal, setAddParentModal] = useState(false);
    const [addParentForm, setAddParentForm] = useState({ name: '' });
    const [addParentErrors, setAddParentErrors] = useState({});
    const [addParentLoading, setAddParentLoading] = useState(false);

    useEffect(() => {
        fetchRadiologies();
    }, []);

    useEffect(() => {
        if (selectedParent) {
            loadChildRadiologies(selectedParent.id);
        }
    }, [selectedParent]);

    const fetchRadiologies = () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        axios.get(`${process.env.REACT_APP_API_URL}/radiology`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                const allRadiologies = res.data;
                const parents = allRadiologies.filter(item => !item.parent_id);
                setParentRadiologies(parents);

                if (parents.length > 0 && !selectedParent) {
                    setSelectedParent(parents[0]);
                } else if (selectedParent) {
                    const updatedParent = parents.find(p => p.id === selectedParent.id);
                    if (updatedParent) {
                        setSelectedParent(updatedParent);
                    }
                }
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    const loadChildRadiologies = (parentId) => {
        const token = localStorage.getItem("authToken");
        axios.get(`${process.env.REACT_APP_API_URL}/radiology`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
            const children = res.data.filter(item => item.parent_id === parentId);
            setChildRadiologies(children);
        }).catch(err => console.error(err));
    };

    // Add Parent Modal Functions
    const openAddParentModal = () => {
        setAddParentForm({ name: '' });
        setAddParentErrors({});
        setAddParentModal(true);
    };

    const handleAddParentChange = (e) => {
        const { name, value } = e.target;
        setAddParentForm(prev => ({ ...prev, [name]: value }));
        if (addParentErrors[name]) {
            setAddParentErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleAddParentSubmit = async (e) => {
        e.preventDefault();
        setAddParentLoading(true);
        setAddParentErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/radiology`,
                { name: addParentForm.name, parent_id: null, price: null, cost_price: null },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.radiology) {
                fetchRadiologies();
                setAddParentModal(false);
                setAddParentForm({ name: '' });

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Radiology category created successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setAddParentErrors(error.response.data.errors || {});

                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error creating radiology category:', error);

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to create radiology category'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setAddParentLoading(false);
        }
    };

    // Store Modal Functions (for adding child radiology under selected parent)
    const openStoreModal = () => {
        if (!selectedParent) {
            Swal.fire({
                icon: 'warning',
                title: t('warning'),
                text: t('Please select a category first'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
            return;
        }
        setStoreForm({ name: '', price: '', cost_price: '' });
        setStoreErrors({});
        setStoreModal(true);
    };

    const handleStoreChange = (e) => {
        const { name, value } = e.target;
        setStoreForm(prev => ({ ...prev, [name]: value }));
        if (storeErrors[name]) {
            setStoreErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleStoreSubmit = async (e) => {
        e.preventDefault();
        setStoreLoading(true);
        setStoreErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/radiology`,
                {
                    name: storeForm.name,
                    price: storeForm.price,
                    cost_price: storeForm.cost_price,
                    parent_id: selectedParent.id
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.radiology) {
                fetchRadiologies();
                setStoreModal(false);
                setStoreForm({ name: '', price: '', cost_price: '' });

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Radiology service created successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setStoreErrors(error.response.data.errors || {});

                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error creating radiology:', error);

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to create radiology service'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setStoreLoading(false);
        }
    };

    // Update Child Modal Functions
    const openUpdateModal = (item) => {
        setUpdateForm({
            id: item.id,
            name: item.name,
            price: item.price || '',
            cost_price: item.cost_price || ''
        });
        setUpdateErrors({});
        setUpdateModal(true);
    };

    const handleUpdateChange = (e) => {
        const { name, value } = e.target;
        setUpdateForm(prev => ({ ...prev, [name]: value }));
        if (updateErrors[name]) {
            setUpdateErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setUpdateLoading(true);
        setUpdateErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/radiology-update/${updateForm.id}`,
                {
                    name: updateForm.name,
                    price: updateForm.price,
                    cost_price: updateForm.cost_price,
                    parent_id: selectedParent?.id
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.radiology) {
                fetchRadiologies();
                setUpdateModal(false);

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Radiology service updated successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setUpdateErrors(error.response.data.errors || {});

                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else if (error.response?.status === 404) {
                setUpdateErrors({ general: [error.response.data.error] });

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response.data.error || t('Service not found'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error updating radiology:', error);

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to update radiology service'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setUpdateLoading(false);
        }
    };

    // Update Parent Modal Functions
    const openUpdateParentModal = (parent) => {
        setUpdateParentForm({
            id: parent.id,
            name: parent.name
        });
        setUpdateParentErrors({});
        setUpdateParentModal(true);
    };

    const handleUpdateParentChange = (e) => {
        const { name, value } = e.target;
        setUpdateParentForm(prev => ({ ...prev, [name]: value }));
        if (updateParentErrors[name]) {
            setUpdateParentErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateParentSubmit = async (e) => {
        e.preventDefault();
        setUpdateParentLoading(true);
        setUpdateParentErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/radiology-update/${updateParentForm.id}`,
                {
                    name: updateParentForm.name,
                    price: null,
                    cost_price: null,
                    parent_id: null
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data.radiology) {
                fetchRadiologies();
                setUpdateParentModal(false);

                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Radiology category updated successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setUpdateParentErrors(error.response.data.errors || {});

                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else if (error.response?.status === 404) {
                setUpdateParentErrors({ general: [error.response.data.error] });

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response.data.error || t('Category not found'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error updating radiology category:', error);

                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to update radiology category'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setUpdateParentLoading(false);
        }
    };

    // Delete Function - FIXED (accepts id and type as parameters)
    const handleDelete = async (id, type) => {
        setDeleteLoading(true);
        const token = localStorage.getItem("authToken");

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/radiology-delete/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchRadiologies();

            Swal.fire({
                icon: 'success',
                title: t('deleted'),
                text: type === 'parent'
                    ? t('Radiology category deleted successfully')
                    : t('Radiology service deleted successfully'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } catch (error) {
            console.error('Error deleting radiology:', error);

            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('Failed to delete'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } finally {
            setDeleteLoading(false);
            setCurrentId(null);
            setCurrentType(null);
        }
    };

    // Delete confirmation with SweetAlert - FIXED
    const confirmDelete = (id, type) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: type === 'parent'
                ? t("Want to delete this radiology category?")
                : t("Want to delete this radiology service?"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('delete'),
            cancelButtonText: t('cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                handleDelete(id, type); // Pass parameters directly
            }
        });
    };

    if (loading) return <PageLayout><p>{t("loading")}...</p></PageLayout>;

    return (
        <PageLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 style={{ color: "#777" }}>Radiology Services</h4>
                <ButtonComponent
                    className="btn"
                    style={{ backgroundColor: "#3182ce", color: "#fff" }}
                    onClick={openAddParentModal}
                >
                    + Add Category
                </ButtonComponent>
            </div>

            <div className="card-container">
                <Row className="g-0">
                    {/* LEFT COLUMN: Categories (Parent Radiologies) */}
                    <Col md={4} className="border-end">
                        <div className="header-strip">
                            <span>
                                <i className="material-icons align-middle me-2" style={{ fontSize: '18px' }}>radiology</i>
                                Radiology Categories
                            </span>
                        </div>
                        <div style={{ height: "70vh", overflowY: "auto" }}>
                            {parentRadiologies.length === 0 ? (
                                <div className="text-center text-muted py-4">
                                    No categories found
                                </div>
                            ) : (
                                parentRadiologies.map((parent) => (
                                    <div
                                        key={parent.id}
                                        className={`sidebar-item ${selectedParent?.id === parent.id ? 'active' : ''}`}
                                        onClick={() => setSelectedParent(parent)}
                                    >
                                        <i
                                            className="material-icons delete-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                confirmDelete(parent.id, 'parent');
                                            }}
                                        >delete</i>
                                        <i
                                            className="material-icons edit-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openUpdateParentModal(parent);
                                            }}
                                        >edit</i>
                                        <span className={selectedParent?.id === parent.id ? 'text-primary' : ''}>
                                            {parent.name}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </Col>

                    {/* RIGHT COLUMN: Values (Child Radiologies) */}
                    <Col md={8} className="p-4">
                        <div className="d-flex justify-content-end mb-3">
                            <ButtonComponent
                                className="btn btn-sm"
                                style={{ backgroundColor: "#3182ce", color: "#fff" }}
                                onClick={openStoreModal}
                                disabled={!selectedParent}
                            >
                                + Add Service
                            </ButtonComponent>
                        </div>

                        {!selectedParent ? (
                            <div className="text-center text-muted py-5">
                                <i className="material-icons" style={{ fontSize: '48px' }}>radiology</i>
                                <p className="mt-2">Select a category from the left to view its services</p>
                            </div>
                        ) : childRadiologies.length === 0 ? (
                            <div className="text-center text-muted py-5">
                                <i className="material-icons" style={{ fontSize: '48px' }}>inbox</i>
                                <p className="mt-2">No services found for this category</p>
                                <ButtonComponent
                                    className="btn btn-sm mt-2"
                                    style={{ backgroundColor: "#3182ce", color: "#fff" }}
                                    onClick={openStoreModal}
                                >
                                    + Add First Service
                                </ButtonComponent>
                            </div>
                        ) : (
                            <Table responsive hover borderless>
                                <thead>
                                    <tr className="table-header">
                                        <th>Service Name</th>
                                        <th>Price</th>
                                        <th>Cost Price</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {childRadiologies.map((child) => (
                                        <tr key={child.id} className="table-row">
                                            <td className="align-middle">
                                                <i
                                                    className="material-icons delete-icon"
                                                    onClick={() => confirmDelete(child.id, 'child')}
                                                >delete</i>
                                                {child.name}
                                            </td>
                                            <td className="align-middle">
                                                {child.price ? `${child.price}` : '-'}
                                            </td>
                                            <td className="align-middle">
                                                {child.cost_price ? `${child.cost_price}` : '-'}
                                            </td>
                                            <td className="text-end align-middle">
                                                <button
                                                    className="action-btn"
                                                    onClick={() => openUpdateModal(child)}
                                                >
                                                    <i className="material-icons" style={{ fontSize: "16px" }}>edit_note</i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Col>
                </Row>
            </div>

            {/* Add Parent Modal - Only Name Field */}
            <Modal
                show={addParentModal}
                onHide={() => setAddParentModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        Add Radiology Category
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddParentSubmit}>
                    <Modal.Body className="custom-modal-body">
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={addParentForm.name}
                                onChange={handleAddParentChange}
                                isInvalid={!!addParentErrors.name}
                                placeholder="Enter category name"
                                autoFocus
                            />
                            <Form.Control.Feedback type="invalid">
                                {addParentErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setAddParentModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={addParentLoading}
                        >
                            {addParentLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Save
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Store Modal - Add Service (Child) with Name and Price */}
            <Modal
                show={storeModal}
                onHide={() => setStoreModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        Add Radiology Service
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleStoreSubmit}>
                    <Modal.Body className="custom-modal-body">
                        {selectedParent && (
                            <div className="alert alert-info mb-3">
                                <i className="material-icons me-2" style={{ fontSize: '16px' }}>info</i>
                                Adding service under: <strong>{selectedParent.name}</strong>
                            </div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={storeForm.name}
                                onChange={handleStoreChange}
                                isInvalid={!!storeErrors.name}
                                placeholder="Enter service name"
                                autoFocus
                            />
                            <Form.Control.Feedback type="invalid">
                                {storeErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Price
                            </Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                name="price"
                                className="custom-input"
                                value={storeForm.price}
                                onChange={handleStoreChange}
                                isInvalid={!!storeErrors.price}
                                placeholder="Enter price (optional)"
                            />
                            <Form.Control.Feedback type="invalid">
                                {storeErrors.price?.[0]}
                            </Form.Control.Feedback>
                            <small className="text-muted">Price is optional for radiology services</small>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Cost Price
                            </Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                name="cost_price"
                                className="custom-input"
                                value={storeForm.cost_price}
                                onChange={handleStoreChange}
                                isInvalid={!!storeErrors.cost_price}
                                placeholder="Enter Cost price (optional)"
                            />
                            <Form.Control.Feedback type="invalid">
                                {storeErrors.cost_price?.[0]}
                            </Form.Control.Feedback>
                            <small className="text-muted">Cost price is optional for radiology services</small>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setStoreModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={storeLoading}
                        >
                            {storeLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Save
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Update Child Modal - Edit Service */}
            <Modal
                show={updateModal}
                onHide={() => setUpdateModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        Edit Radiology Service
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateSubmit}>
                    <Modal.Body className="custom-modal-body">
                        {updateErrors.general && (
                            <div className="alert alert-danger mb-3">
                                {updateErrors.general}
                            </div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={updateForm.name}
                                onChange={handleUpdateChange}
                                isInvalid={!!updateErrors.name}
                                placeholder="Enter service name"
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Price
                            </Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                name="price"
                                className="custom-input"
                                value={updateForm.price}
                                onChange={handleUpdateChange}
                                isInvalid={!!updateErrors.price}
                                placeholder="Enter price (optional)"
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateErrors.price?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Cost Price
                            </Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                name="cost_price"
                                className="custom-input"
                                value={updateForm.cost_price}
                                onChange={handleUpdateChange}
                                isInvalid={!!updateErrors.cost_price}
                                placeholder="Enter cost price (optional)"
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateErrors.cost_price?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setUpdateModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={updateLoading}
                        >
                            {updateLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Update
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Update Parent Modal - Edit Category Name Only */}
            <Modal
                show={updateParentModal}
                onHide={() => setUpdateParentModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        Edit Radiology Category
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateParentSubmit}>
                    <Modal.Body className="custom-modal-body">
                        {updateParentErrors.general && (
                            <div className="alert alert-danger mb-3">
                                {updateParentErrors.general}
                            </div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={updateParentForm.name}
                                onChange={handleUpdateParentChange}
                                isInvalid={!!updateParentErrors.name}
                                placeholder="Enter category name"
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateParentErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setUpdateParentModal(false)}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn px-4"
                            style={{ backgroundColor: "#3182ce", color: "#fff" }}
                            disabled={updateParentLoading}
                        >
                            {updateParentLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            Update
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}